import { inject, Injectable, Signal } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { createActionResponseFactory } from '@core/ngrx-legacy';
import {
  TellentPositionsActions,
  TellentPositionsHttpClient,
  TellentPositionsSelectors,
} from '@core/tellent-positions-data';
import {
  GeneratePositionDescriptionPayload,
  GeneratePositionDescriptionResponse,
  TellentPosition,
  UpdatePositionPayload,
} from '@core/tellent-positions-types';
import { generateUuid } from '@core/utils-legacy';

@Injectable({ providedIn: 'root' })
export class TellentPositionsDataFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly positionsActionResponse = createActionResponseFactory(TellentPositionsActions);
  private readonly httpClient = inject(TellentPositionsHttpClient);

  public readonly positionsData: Signal<TellentPosition[]> = this.store.selectSignal(
    TellentPositionsSelectors.entities(),
  );

  public getPositionById(guid: string) {
    return this.store.selectSignal(TellentPositionsSelectors.byId(guid));
  }

  public fetchPositions(): void {
    this.store.dispatch(TellentPositionsActions.fetchCollection());
  }

  public createPosition(name: string) {
    return this.positionsActionResponse(
      TellentPositionsActions.createOnePessimistic,
      { payload: { name, correlationId: generateUuid() } },
      {
        success: TellentPositionsActions.createOnePessimisticSuccess,
        failed: TellentPositionsActions.createOnePessimisticFailed,
      },
    );
  }

  public fetchOne(guid: string) {
    this.store.dispatch(TellentPositionsActions.fetchOne({ payload: { id: guid } }));
  }

  public renamePosition(guid: string, name: string) {
    return this.updatePosition(guid, { name });
  }

  public updateDescription(guid: string, descriptionJson: unknown | null) {
    return this.updatePosition(guid, { descriptionJson });
  }

  public generateDescription(
    payload: GeneratePositionDescriptionPayload,
  ): Observable<GeneratePositionDescriptionResponse> {
    return this.httpClient.generateDescription(payload);
  }

  public deletePosition(guid: string) {
    return this.positionsActionResponse(
      TellentPositionsActions.removeOnePessimistic,
      { payload: { guid } },
      {
        success: TellentPositionsActions.removeOnePessimisticSuccess,
        failed: TellentPositionsActions.removeOnePessimisticFailed,
      },
    );
  }

  public assignMembers(positionGuid: string, membersGuids: string[]) {
    return this.positionsActionResponse(
      TellentPositionsActions.assignMembers,
      { payload: { positionGuid, membersGuids } },
      {
        success: TellentPositionsActions.assignMembersSuccess,
        failed: TellentPositionsActions.assignMembersFailed,
      },
    );
  }

  public unassignMembersFromPosition(positionGuid: string, membersGuids: string[]) {
    return this.positionsActionResponse(
      TellentPositionsActions.unassignMembers,
      { payload: { positionGuid, membersGuids } },
      {
        success: TellentPositionsActions.unassignMembersSuccess,
        failed: TellentPositionsActions.unassignMembersFailed,
      },
    );
  }

  public getPositionsChangeAction() {
    return this.actions$.pipe(
      ofType(
        TellentPositionsActions.assignMembersSuccess,
        TellentPositionsActions.unassignMembersSuccess,
      ),
    );
  }

  private updatePosition(guid: string, position: UpdatePositionPayload) {
    return this.positionsActionResponse(
      TellentPositionsActions.updateOnePessimistic,
      { payload: { guid, position } },
      {
        success: TellentPositionsActions.updateOnePessimisticSuccess,
        failed: TellentPositionsActions.updateOnePessimisticFailed,
      },
    );
  }
}
